import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { ArrowLeft, Shield } from 'lucide-react';
import BottomNav from '../components/BottomNav';

const Privacy = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleBack = () => {
    // If user is logged in, go back to settings, otherwise go to landing
    if (user) {
      navigate('/settings');
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-teal-50 dark:from-slate-900 dark:to-slate-800 py-8 px-4 transition-colors">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{user ? 'Back to Settings' : 'Back to Home'}</span>
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 dark:from-cyan-600 dark:to-blue-500 flex items-center justify-center transition-colors">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-400 dark:to-blue-400 bg-clip-text text-transparent transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>
              Privacy Policy
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 transition-colors dark:text-gray-400 transition-colors">Last updated: October 18, 2025</p>
        </div>

        {/* Content */}
        <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 p-8 transition-colors">
          <div className="prose prose-slate max-w-none">
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">1. Introduction</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors dark:text-gray-300 leading-relaxed transition-colors">
                Welcome to Logly ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our time tracking application and services.
              </p>
              <p className="text-gray-700 dark:text-gray-300 transition-colors dark:text-gray-300 leading-relaxed mt-3 transition-colors">
                By using Logly, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors">2.1 Personal Information</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors dark:text-gray-300 leading-relaxed mb-3 transition-colors">
                We collect information that you provide directly to us when you:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 transition-colors dark:text-gray-300 transition-colors">
                <li>Create an account (email address, password)</li>
                <li>Subscribe to our services (payment information via Stripe)</li>
                <li>Use our voice recording features (audio recordings, transcriptions)</li>
                <li>Create projects and time entries (project names, descriptions, durations)</li>
                <li>Contact us for support (name, email, message content)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 mt-6 transition-colors">2.2 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 transition-colors dark:text-gray-300 transition-colors">
                <li>Device information (browser type, operating system)</li>
                <li>Usage data (features used, time spent, interactions)</li>
                <li>Log data (IP address, access times, pages viewed)</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 mt-6 transition-colors">2.3 Voice Data</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors dark:text-gray-300 leading-relaxed transition-colors">
                When you use our voice logging feature, we temporarily process your audio recordings using third-party AI services (OpenAI) to transcribe and parse your time entries. Audio recordings are not permanently stored after processing is complete.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">3. How We Use Your Information</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors dark:text-gray-300 leading-relaxed mb-3 transition-colors">
                We use your information to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 transition-colors dark:text-gray-300 transition-colors">
                <li>Provide, maintain, and improve our services</li>
                <li>Process your time entries and generate reports</li>
                <li>Process payments and manage subscriptions</li>
                <li>Send you service-related notifications and updates</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Train our AI models to improve project predictions</li>
                <li>Detect and prevent fraud and abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">4. Information Sharing and Disclosure</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors">4.1 Third-Party Service Providers</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed mb-3">
                We share your information with trusted third-party service providers who assist us in operating our services:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 transition-colors">
                <li><strong>Stripe:</strong> Payment processing and subscription management</li>
                <li><strong>OpenAI:</strong> Voice transcription and AI processing</li>
                <li><strong>Cloud hosting providers:</strong> Data storage and infrastructure</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors mt-6">4.2 Legal Requirements</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                We may disclose your information if required by law, court order, or government request, or to protect our rights, property, or safety.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors mt-6">4.3 Business Transfers</h3>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">5. Data Security</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                We implement appropriate technical and organizational security measures to protect your information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 transition-colors mt-3">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication and password hashing</li>
                <li>Regular security audits and updates</li>
                <li>Restricted access to personal information</li>
                <li>Monitoring for security breaches</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed mt-3">
                However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">6. Your Rights and Choices</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed mb-3">
                You have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 transition-colors">
                <li><strong>Access:</strong> Request a copy of your personal information</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                <li><strong>Export:</strong> Download your time entries in CSV or PDF format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>AI Processing:</strong> Disable AI project predictions in Settings</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed mt-3">
                To exercise these rights, please contact us at <a href="mailto:support@logly.work" className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors">support@logly.work</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">7. Data Retention</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                We retain your information for as long as necessary to provide our services and comply with legal obligations. When you delete your account, we will delete or anonymize your personal information within 30 days, except where we are required to retain it for legal purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">8. Children's Privacy</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                Logly is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected information from a child under 13, we will take steps to delete such information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">9. International Data Transfers</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of residence. We ensure that appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">10. Cookies and Tracking</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                We use cookies and similar technologies to enhance your experience, analyze usage, and deliver personalized content. You can control cookies through your browser settings, but disabling them may affect functionality.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">11. Changes to This Policy</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of Logly after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4 transition-colors">12. Contact Us</h2>
              <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                <p className="text-gray-700 dark:text-gray-300 transition-colors"><strong>Email:</strong> <a href="mailto:support@logly.work" className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors">support@logly.work</a></p>
                <p className="text-gray-700 dark:text-gray-300 transition-colors mt-1"><strong>Website:</strong> <a href="https://logly.work" className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors">https://logly.work</a></p>
              </div>
            </section>

          </div>
        </Card>
      </div>

      <BottomNav />
    </div>
  );
};

export default Privacy;
